import type { AxiosRequestConfig } from 'axios'
import merge from 'lodash.merge'

import type {
  LoggerActivationId,
  LoggerActivationInitializer,
  LoggerActivationMutator,
  LoggerEventInitializer,
  LoggerHistoryId,
  LoggerHistoryInitializer,
  LoggerHistoryMutator,
  LoggerId,
  LoggerWithContextInitializer,
  LoggerWithContextMutator,
} from '@beaded/models'

import type { Obj } from 'types/common'

import * as apiUtils from '.'

const url = '/logger'

export const get = (id: LoggerId | string, options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/${id}`, merge({}, apiUtils.extrasFlags, options))

export interface LoggerEventsPageParams {
  /** Comma-separated event types; `status` covers every `status.*` type. */
  types?: string
  limit?: number
  offset?: number
}

/** One page of a logger's events, newest first, with `paging.total`. */
export const getEvents = (
  id: LoggerId,
  page: LoggerEventsPageParams,
  options: AxiosRequestConfig = {},
) => apiUtils.GET(`${url}/${id}/events`, merge({ params: page }, options))

/** One Iridium message of a logger, as decoded into PostgreSQL. */
export interface LoggerMessage {
  ingestionId: string
  /** ISO time the message was received. */
  tsReceived: string
  momsn: number | null
  /** False when no sensor rows were decoded for the message. */
  hasData: boolean
  logger: Array<{
    ts: string | null
    battery: number | null
    temperature: { min: number | null; max: number | null }
    humidity: number | null
  }>
  cable: Array<{
    serial: string
    ts: string | null
    readings: Array<number | null>
  }>
  /** Absent when the server does not return sonic data. */
  sonic?: Array<{ ts: string | null; range: number | null }>
}

export interface LoggerMessagesResponse {
  messages: LoggerMessage[]
}

/** One logger's last transmission and its 7-day battery trend. */
export interface LoggerLastTransmission {
  loggerSerial: string
  imei: string | null
  receivedAt: string | null
  /** `timestamp` is epoch ms. */
  batteryTrend: Array<{ timestamp: number; battery: number }>
  msgSize: number | null
}

export interface LoggerLastTransmissionsResponse {
  transmissions: LoggerLastTransmission[]
}

/**
 * The newest 100 Iridium messages of a logger, newest first. Global-view
 * staff only; any other caller gets a 403.
 */
export const getMessages = (id: LoggerId, options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/${id}/messages`, options)

/**
 * The last transmission of every logger, with a 7-day battery trend.
 * Global-view staff only; any other caller gets a 403.
 */
export const getLastTransmissions = (options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/last-transmissions`, options)

export const getAll = (options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/`, merge({}, apiUtils.extrasFlags, options))

export const create = (data: LoggerWithContextInitializer) =>
  apiUtils.POST(`${url}/`, data)

export const createEvent = (loggerId: LoggerId, data: LoggerEventInitializer) =>
  apiUtils.PUT(`${url}/${loggerId}/event`, data)

export const createHistory = (
  loggerId: LoggerId,
  data: LoggerHistoryInitializer,
) => apiUtils.POST(`${url}/${loggerId}/history`, data)

export const bulkUpdateHistory = (
  loggerId: LoggerId,
  data: LoggerHistoryMutator[],
) => apiUtils.PUT(`${url}/${loggerId}/history`, data)

export const updateHistory = (
  loggerId: LoggerId,
  loggerHistoryId: LoggerHistoryId,
  data: LoggerHistoryMutator,
) => apiUtils.PUT(`${url}/${loggerId}/history/${loggerHistoryId}`, data)

export const removeHistory = (
  loggerId: LoggerId,
  loggerHistoryId: LoggerHistoryId,
) => apiUtils.DELETE(`${url}/${loggerId}/history/${loggerHistoryId}`)

export const update = (id: LoggerId, data: LoggerWithContextMutator) =>
  apiUtils.PUT(`${url}/${id}`, data)

export const patch = (id: LoggerId, data: Obj) =>
  apiUtils.PATCH(`${url}/${id}`, data)

export const remove = (id: LoggerId) => apiUtils.DELETE(`${url}/${id}`)

export const getMenuProjection = (options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/`, merge({ params: { projection: 'menu' } }, options))

export const validateSerial = (serial: string) =>
  apiUtils.GET(`${url}/validate`, { params: { serial } })

export const createLoggerActivation = (
  id: LoggerId,
  data: LoggerActivationInitializer,
  options: AxiosRequestConfig = {},
) => apiUtils.POST(`${url}/${id}/activation`, data, options)

export const updateLoggerActivation = (
  id: LoggerId,
  activationId: LoggerActivationId,
  data: LoggerActivationMutator,
  options: AxiosRequestConfig = {},
) => apiUtils.PUT(`${url}/${id}/activation/${activationId}`, data, options)

export const sendSbdMessage = (
  id: LoggerId,
  message: string,
  options: AxiosRequestConfig = {},
) => apiUtils.PUT(`${url}/${id}/sbd`, { id, message }, options)

export const getManufacturers = (): Promise<{ manufacturers: string[] }> =>
  apiUtils.GET(`${url}/manufacturers`)

export const createApiKey = (
  loggerId: LoggerId,
): Promise<{ api_key: string; expires_at: string }> =>
  apiUtils.POST(`${url}/${loggerId}/api-key`, {})

export const getApiKey = (
  loggerId: LoggerId,
): Promise<{
  api_key: string
  logger_id: number
  created_at: string
  expires_at: string
  last_used_at: string | null
  last_used_ip: string | null
}> => apiUtils.GET(`${url}/${loggerId}/api-key`)

export const replaceApiKey = (
  loggerId: LoggerId,
): Promise<{ api_key: string; expires_at: string; replaced_key_id: string }> =>
  apiUtils.POST(`${url}/${loggerId}/api-key/replace`, {})
